import { createAsyncThunk } from '@reduxjs/toolkit';

import { getAccountIsHub, getCurrentLocationId } from 'selectors/session';

import { getExperimentsEligibleForApi } from 'features/experiments/selectors';

import getIsEmbedded from 'util/embedded';
import { amplitudeIdentify } from 'util/trackingModules/amplitude';

import { putEnrollEntityInExperiment } from './api';
import { SLICE_NAME } from './constants';

export const actionTypes = {
  EXPERIMENT_RELOAD: 'EXPERIMENT/EXPERIMENT_RELOAD',
};

/**
 * Thunk to enroll an entity (e.g., user or location) in one or more experiments.
 *
 * - Checks if the entity is eligible for enrollment.
 * - If not eligible, returns an object mapping each experiment to 0 (not enrolled).
 * - Otherwise, calls the API to enroll and handles analytics tracking.
 */
export const enrollEntityInExperiment = createAsyncThunk(
  `${SLICE_NAME}/entityEnroll`,
  (experiments, { rejectWithValue, getState }) => {
    // Get the Redux state
    const state = getState();
    const isEmbedded = getIsEmbedded();

    // If the current company is clover embedded, do not enroll them in any experiments
    if (isEmbedded)
      return experiments.reduce((result, experiment) => {
        result[experiment] = 0;
        return result;
      }, {});

    // Determine which experiments are eligible for API enrollment
    const eligibleExperiments = getExperimentsEligibleForApi(
      state,
      experiments
    );

    // Get the current location and account type
    const currentLocationId = getCurrentLocationId(state);
    const isHubAccount = getAccountIsHub(state);

    // If not eligible (hub account, no location, or no eligible experiments), return early
    const isNotEligible =
      isHubAccount || !currentLocationId || eligibleExperiments.length === 0;
    if (isNotEligible) {
      // Return an object mapping each experiment to 0 (not enrolled)
      return experiments.reduce((result, experiment) => {
        result[experiment] = 0;
        return result;
      }, {});
    }

    // Otherwise, call the API to enroll the entity in the experiments
    return putEnrollEntityInExperiment(experiments)
      .then(({ amplitudeGroupIdentifyData, viewLogic }) => {
        // If analytics data is returned, send it to Amplitude for tracking
        if (amplitudeGroupIdentifyData) {
          amplitudeIdentify({ ...amplitudeGroupIdentifyData });
        }
        // Return the updated view logic
        return viewLogic;
      })
      .catch(error =>
        // Handle errors by passing a meaningful message to Redux
        rejectWithValue(error.body || error.message)
      );
  }
);

export const updateReloadExperiment = reloadExperiment => ({
  type: actionTypes.EXPERIMENT_RELOAD,
  payload: reloadExperiment,
});
